// packages/console/src/lib/probeGraph.ts
//
// Pure helpers for the admin probe bit-distribution graph: resolving the CABA
// bits/sec target that is active at a given time, and step-interpolating it
// across the graph's bin time axis so it can ride as a uPlot series. Kept out
// of the panel component so they stay unit-testable and the panel file exports
// only components.

import type { ProbeGraph } from "../api/jobs";

export type CabaSteps = ProbeGraph["cabaTarget"]["steps"];

// stepValueAt returns the CABA target bits/sec active at time t (the step whose
// [startSeconds,endSeconds) contains t), or null when no step covers t. A time
// PAST the last step's end is a genuine gap (the CABA plan stopped covering t),
// so it returns null rather than extrapolating the last target forever, which
// would fabricate a value where none exists (R10). The exact final endSeconds
// is treated as still inside the last step so the track reaches its own end.
export function stepValueAt(steps: CabaSteps, t: number): number | null {
  for (const s of steps) {
    if (t >= s.startSeconds && t < s.endSeconds) return s.targetBitsPerSec;
  }
  const last = steps[steps.length - 1];
  if (last && t === last.endSeconds) return last.targetBitsPerSec;
  return null;
}

// cabaTrack step-interpolates the CABA target across the bin time axis so it can
// ride as a uPlot series on the second y-axis. Each bin time gets the target
// active at that instant; a gap (no covering step) is a null (uPlot skips it).
export function cabaTrack(steps: CabaSteps, t: number[]): (number | null)[] {
  return t.map((tt) => stepValueAt(steps, tt));
}
